import { NAME } from '@app/marketing/constants';

// Utils
import imgix from '@core/imgix';

// Hooks
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { useSelector, useDispatch } from 'react-redux';

// Redux
import {
    fetchFunnelDetails,
    getError,
    getFunnelDetails,
    getLoading,
} from '@app/marketing/models/sharedFunnel';

// UI
import Headline from '@ui/components/Headline';
import Subtitle from '@ui/components/Subtitle';
import PlayButton from '@app/marketing/components/Testimonials/PlayButton';
import VideoOverlay from '@ui/components/VideoOverlay';
import LoadingSkeleton from './Loading';
import Arrow from './Arrow';
import Error from './Error';

// Types
import type { FC } from 'react';

export interface Props {}

const SharedFunnelDetails: FC<Props> = ({}) => {
    const { t } = useTranslation(NAME);
    const dispatch = useDispatch();
    const {
        query: { campaignId },
    } = useRouter();

    const funnelDetails = useSelector(getFunnelDetails);
    const loading = useSelector(getLoading);
    const error = useSelector(getError);

    const [videoPlaying, setVideoPlaying] = useState<string>('');

    // Fetch Funnel Details
    useEffect(() => {
        dispatch(fetchFunnelDetails(campaignId as string));
    }, [dispatch, campaignId]);

    const handlePlay = (url: string) => {
        setVideoPlaying(url);
    };

    const handleClose = () => setVideoPlaying('');

    return (
        <div data-cy="funnel-details">
            {!loading && !error ? (
                <>
                    <Headline>{t('shared-funnel-headline', { name: funnelDetails.name })}</Headline>
                    <Subtitle>{t('shared-funnel-subtitle')}</Subtitle>

                    <div
                        className="relative mx-auto mt-12 flex aspect-video w-80 items-center justify-center rounded-lg bg-cover bg-no-repeat"
                        style={{ backgroundImage: `url(${imgix.shared.sharedVideoBg})` }}
                    >
                        <PlayButton url={t('shared-funnel-video-url')} onClick={handlePlay} />
                    </div>

                    <Arrow />

                    {videoPlaying && <VideoOverlay url={videoPlaying} onClose={handleClose} />}
                </>
            ) : error ? (
                <Error />
            ) : (
                <LoadingSkeleton />
            )}
        </div>
    );
};

export default SharedFunnelDetails;
